import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@remix-run/react";
import { ClientOnly } from "@core/components/client-only";
import { EditorSection, useEditorFrameContext } from "@core/components/EditorFrame";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { useApi } from "@core/api";
import { Button } from "@core/components/ui/button";
import { Checkbox } from "@core/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@core/components/ui/collapsible";
import { useToast } from "@core/components/ui/use-toast";
import { ChevronRight } from "lucide-react";
import { cn } from "@core/lib/utils";
import type { CustomerProductAssignmentRootDto } from "@/apidtos";

function assignmentSignature(ids: Iterable<number>): string {
  return [...ids].sort((a, b) => a - b).join(",");
}

const CustomerProductAssignmentsInner = ({ customerId }: { customerId: number }) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { setAccordionValue, accordionValue } = useEditorFrameContext();

  const sectionTitle = t("ui.customerProductAssignments", "Product assignments");

  useEffect(() => {
    if (!accordionValue.includes(sectionTitle)) {
      setAccordionValue((prev) => [...prev, sectionTitle]);
    }
  }, [accordionValue, sectionTitle, setAccordionValue]);

  const q = useQuery({
    queryKey: ["customer-product-assignments", customerId],
    queryFn: () => api.GetCustomerProductAssignments(customerId),
  });

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [openRoots, setOpenRoots] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const data = q.data;
    if (data?.AssignedProductIds) {
      setSelected(new Set(data.AssignedProductIds));
    }
  }, [q.data]);

  const allSelectableIds = useMemo(() => {
    const ids: number[] = [];
    for (const r of q.data?.Roots ?? []) {
      ids.push(r.ProductId);
      for (const v of r.VariantProducts ?? []) {
        ids.push(v.ProductId);
      }
    }
    return ids;
  }, [q.data]);

  const baseline = useMemo(
    () => assignmentSignature(q.data?.AssignedProductIds ?? []),
    [q.data?.AssignedProductIds],
  );
  const currentSig = useMemo(() => assignmentSignature(selected), [selected]);
  const isDirty = baseline !== currentSig;

  const toggleId = useCallback((id: number, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const selectAllChecked =
    allSelectableIds.length > 0 && allSelectableIds.every((id) => selected.has(id));
  const selectAllIndeterminate =
    !selectAllChecked && allSelectableIds.some((id) => selected.has(id));

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.PutCustomerProductAssignments(customerId, {
        ProductIds: [...selected].sort((a, b) => a - b),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["customer-product-assignments", customerId] });
      toast({
        title: t("ui.saved", "Saved"),
        description: t(
          "ui.customerProductAssignments.saved",
          "Product selections were updated for this customer.",
        ),
      });
    },
    onError: () => {
      toast({
        title: t("ui.error", "Error"),
        description: t(
          "ui.customerProductAssignments.saveFailed",
          "Could not save product assignments.",
        ),
        variant: "destructive",
      });
    },
  });

  const setSelectAll = (checked: boolean) => {
    if (checked) {
      setSelected(new Set(allSelectableIds));
    } else {
      setSelected(new Set());
    }
  };

  if (q.isLoading || q.isError || !q.data) {
    return (
      <EditorSection title={sectionTitle}>
        <p className="text-sm text-muted-foreground">
          {q.isError
            ? t("ui.customerProductAssignments.loadFailed", "Could not load product assignments.")
            : t("ui.loading", "Loading…")}
        </p>
      </EditorSection>
    );
  }

  const { Roots } = q.data;

  return (
    <EditorSection title={sectionTitle}>
      <div className="flex flex-wrap items-center gap-4 border-b border-border pb-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id="cpa-select-all"
            checked={selectAllIndeterminate ? "indeterminate" : selectAllChecked}
            onCheckedChange={(c) => setSelectAll(c === true)}
          />
          <label htmlFor="cpa-select-all" className="cursor-pointer text-sm font-medium">
            {t("ui.selectAll", "Select all")}
          </label>
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!isDirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {saveMutation.isPending
            ? t("ui.saving", "Saving…")
            : t("ui.saveAssignments", "Save product assignments")}
        </Button>
      </div>

      <div className="mt-3 max-h-[28rem] overflow-y-auto pr-1">
        {Roots.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("ui.customerProductAssignments.emptyCatalog", "No products are available to assign.")}
          </p>
        ) : (
          <>
            <div
              className={cn(
                "bg-surface-50 sticky top-0 z-10 grid items-center gap-2 border-b border-border px-1 py-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground",
                ROW_GRID,
              )}
            >
              <span />
              <span />
              <span>{t("ui.customerProductAssignments.colProduct", "Product")}</span>
              <span>{t("ui.customerProductAssignments.colConcentration", "Concentration")}</span>
              <span>{t("ui.customerProductAssignments.colVolume", "Volume")}</span>
              <span className="text-right">{t("ui.customerProductAssignments.colActions", "")}</span>
            </div>
            <div className="space-y-1 pt-1">
              {Roots.map((root) => (
                <RootRow
                  key={root.ProductId}
                  root={root}
                  selected={selected}
                  toggleId={toggleId}
                  open={openRoots[root.ProductId] ?? false}
                  setOpen={(o) => setOpenRoots((prev) => ({ ...prev, [root.ProductId]: o }))}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </EditorSection>
  );
};

const ROW_GRID =
  "grid-cols-[1.75rem_1.5rem_minmax(0,1fr)_minmax(0,9rem)_minmax(0,7rem)_auto]";

function RootRow({
  root,
  selected,
  toggleId,
  open,
  setOpen,
}: {
  root: CustomerProductAssignmentRootDto;
  selected: Set<number>;
  toggleId: (id: number, checked: boolean) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const variants = root.VariantProducts ?? [];
  const hasVariants = variants.length > 0;

  const onMasterCheck = (checked: boolean) => {
    toggleId(root.ProductId, checked);
    if (hasVariants && checked) {
      setOpen(true);
    }
  };

  if (!hasVariants) {
    return (
      <div className={cn("grid items-start gap-2 rounded-sm px-1 py-1 hover:bg-muted/40", ROW_GRID)}>
        <span />
        <Checkbox
          checked={selected.has(root.ProductId)}
          onCheckedChange={(c) => toggleId(root.ProductId, c === true)}
          id={`cpa-p-${root.ProductId}`}
          className="mt-0.5"
        />
        <label htmlFor={`cpa-p-${root.ProductId}`} className="flex min-w-0 cursor-pointer flex-col gap-0.5 text-sm">
          <span className="truncate font-medium">{root.Name ?? `Product ${root.ProductId}`}</span>
          {root.Sku ? <span className="truncate text-muted-foreground">{root.Sku}</span> : null}
        </label>
        <span className="truncate text-sm">{root.Concentrations ?? "—"}</span>
        <span className="truncate text-sm">{root.Volume ?? "—"}</span>
        <Button variant="ghost" size="sm" className="h-8 shrink-0 px-2 justify-self-end" asChild>
          <Link to={`/admin-dashboard/product-detail?productId=${root.ProductId}`}>{t("ui.edit", "Edit")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-sm border border-border/80 bg-muted/20">
        <div className={cn("grid items-start gap-2 px-1 py-1", ROW_GRID)}>
          <CollapsibleTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-7 w-7 shrink-0 p-0">
              <ChevronRight className={cn("h-4 w-4 transition-transform", open && "rotate-90")} />
            </Button>
          </CollapsibleTrigger>
          <Checkbox
            checked={selected.has(root.ProductId)}
            onCheckedChange={(c) => onMasterCheck(c === true)}
            id={`cpa-p-${root.ProductId}`}
            className="mt-0.5"
          />
          <label htmlFor={`cpa-p-${root.ProductId}`} className="flex min-w-0 cursor-pointer flex-col gap-0.5 text-sm">
            <span className="truncate font-medium">{root.Name ?? `Product ${root.ProductId}`}</span>
            {root.Sku ? <span className="truncate text-muted-foreground">{root.Sku}</span> : null}
            <span className="truncate text-xs text-muted-foreground">
              {t("ui.customerProductAssignments.expandVariants", "Expand to choose variant SKUs.")}
            </span>
          </label>
          <span className="truncate text-sm">{root.Concentrations ?? "—"}</span>
          <span className="truncate text-sm">{root.Volume ?? "—"}</span>
          <Button variant="ghost" size="sm" className="h-8 shrink-0 px-2 justify-self-end" asChild>
            <Link to={`/admin-dashboard/product-detail?productId=${root.ProductId}`}>{t("ui.edit", "Edit")}</Link>
          </Button>
        </div>
        <CollapsibleContent>
          <div className="space-y-1 border-t border-border/60 bg-background/80 pb-2 pt-2">
            {variants.map((v) => (
              <div key={v.ProductId} className={cn("grid items-start gap-2 px-1 py-0.5", ROW_GRID)}>
                <span />
                <Checkbox
                  checked={selected.has(v.ProductId)}
                  onCheckedChange={(c) => toggleId(v.ProductId, c === true)}
                  id={`cpa-v-${v.ProductId}`}
                  className="mt-0.5"
                />
                <label htmlFor={`cpa-v-${v.ProductId}`} className="flex min-w-0 cursor-pointer flex-col gap-0.5 text-sm">
                  <span className="truncate">{v.Name ?? `Variant ${v.ProductId}`}</span>
                  {v.Sku ? <span className="truncate text-muted-foreground">{v.Sku}</span> : null}
                </label>
                <span className="truncate text-sm">{v.Concentrations ?? "—"}</span>
                <span className="truncate text-sm">{v.Volume ?? "—"}</span>
                <Button variant="ghost" size="sm" className="h-8 shrink-0 px-2 justify-self-end" asChild>
                  <Link to={`/admin-dashboard/product-detail?productId=${v.ProductId}`}>{t("ui.edit", "Edit")}</Link>
                </Button>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

const CustomerProductAssignmentsSection = () => {
  const { id } = useParams();
  const customerId = id ? Number(id) : NaN;
  if (!Number.isFinite(customerId)) {
    return null;
  }
  return <CustomerProductAssignmentsInner customerId={customerId} />;
};

const CustomerProductAssignmentsExtension = () => (
  <ClientOnly>
    <CustomerProductAssignmentsSection />
  </ClientOnly>
);

registerExtension(
  "admin.accounts.customers_.$id.additionalSections",
  CustomerProductAssignmentsExtension,
  "CustomerProductAssignmentsExtension",
);
